import React from 'react';
import { useIntl } from 'react-intl';
import { Box, Typography } from '@mui/material';
import FilterDetail from './FilterDetail';
import { FILTERS_TERMS } from '../Utils/constants';

//keys shown elsewhere (channel in the header) or merged in another row
const HANDLED_KEYS = [
  'IsSMS',
  'Branches',
  'WithWave',
  'Sections',
  'LowerAge',
  'UpperAge',
  'PaidInstallments',
  'Products',
  'PracticeProducts',
  'CustomList',
];

const join = (values) => (values?.length > 0 ? values.join(', ') : null);

const formatFilters = (filters, branches, waveSections, intl) => {
  const {
    Branches,
    WithWave,
    Sections,
    LowerAge,
    UpperAge,
    PaidInstallments,
    Products,
    PracticeProducts,
    CustomList,
  } = filters;

  const rows = [
    {
      key: 'Branches',
      label: 'BRANCHES',
      value: join(
        Branches?.map(
          (id) => branches?.find((b) => b.id === id)?.branchDescription ?? id
        )
      ),
    },
    {
      key: 'WithWave',
      label: 'WAVE',
      value: WithWave
        ? intl.messages.CURRENT_WAVE
        : intl.messages.NOT_CURRENT_WAVE,
    },
    {
      key: 'Sections',
      label: 'SECTIONS',
      value: WithWave
        ? join(
            Sections?.map(
              (id) => waveSections?.find((s) => s.id === id)?.name ?? id
            )
          )
        : null,
    },
    {
      key: 'Age',
      label: 'AGE',
      value:
        LowerAge || UpperAge ? `${LowerAge ?? '-'} - ${UpperAge ?? '-'}` : null,
    },
    {
      key: 'PaidInstallments',
      label: 'PAID_INSTALLMENTS',
      value: PaidInstallments?.rawValue
        ? `${PaidInstallments.rawValue}${PaidInstallments.exclude ? ` (${intl.messages.EXCLUDE_SELECTED})` : ''}`
        : null,
    },
    {
      key: 'PracticeProducts',
      label: 'DESCRIPTION_PRACTICE_PRODUCT',
      value: join(PracticeProducts),
    },
    { key: 'Products', label: 'OFFER_TYPE', value: join(Products) },
    {
      key: 'CustomList',
      label: 'CUSTOMERS',
      value: CustomList?.length > 0 ? String(CustomList.length) : null,
    },
    //remaining (mostly boolean flags): shown only when active
    ...Object.keys(filters)
      .filter((key) => !HANDLED_KEYS.includes(key))
      .map((key) => {
        const value = filters[key];
        return {
          key,
          label: FILTERS_TERMS[key] ?? key,
          value:
            value === true
              ? intl.messages.YES
              : Array.isArray(value)
                ? join(value)
                : typeof value === 'object'
                  ? null //unknown structured filter, nothing readable to show
                  : value || null,
        };
      }),
  ];

  return rows.filter(({ value }) => value !== null && value !== '');
};

const MassiveFiltersStack = function MassiveFiltersStack({
  filters = null,
  waveSections = [],
  branches = [],
}) {
  const intl = useIntl();
  const rows = filters
    ? formatFilters(filters, branches, waveSections, intl)
    : [];

  return rows.length > 0 ? (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '16px',
      }}
    >
      {rows.map(({ key, label, value }) => (
        <FilterDetail key={key} label={label} value={value} />
      ))}
    </Box>
  ) : (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {intl.messages.NO_SELECTION}
    </Typography>
  );
};

export default MassiveFiltersStack;
